export interface GuideRow {
  /** Sintaxe apresentada (ex.: "[ãa]", "{n}"). */
  token: string;
  /** Explicação em uma frase. */
  meaning: string;
  /** Pattern de exemplo, exatamente como seria digitado na regra. */
  pattern: string;
  /** Descrição que casa com o pattern. */
  match: string;
  /** Descrição que NÃO casa com o pattern. */
  noMatch: string;
  /** Observação extra (por que não casa / pegadinha). */
  note?: string;
}

export interface GuideSection {
  title: string;
  rows: GuideRow[];
}

export const GUIDE_SECTIONS: GuideSection[] = [
  {
    title: 'Básico',
    rows: [
      {
        token: 'texto',
        meaning: 'Letras e números casam literalmente, em qualquer parte da descrição.',
        pattern: 'condominio',
        match: 'PAGAMENTO CONDOMINIO TOTAL',
        noMatch: 'PAGAMENTO CONDOM TOTAL',
        note: 'Precisa conter o trecho inteiro.',
      },
      {
        token: 'a|b',
        meaning: 'OU — casa se qualquer uma das alternativas aparecer.',
        pattern: 'petshop|veterinari',
        match: 'CLINICA VETERINARIA SP',
        noMatch: 'PET SHOP DO ZE',
        note: '"PET SHOP" com espaço não é "petshop" — inclua a variação: petshop|pet shop.',
      },
      {
        token: '.',
        meaning: 'Qualquer caractere (exatamente um).',
        pattern: 'cart.o',
        match: 'FATURA CARTAO VISA',
        noMatch: 'FATURA CARTO VISA',
        note: 'Em "cartao" o . casa o "a"; em "carto" falta o caractere do meio.',
      },
    ],
  },
  {
    title: 'Conjuntos [ ]',
    rows: [
      {
        token: '[ab]',
        meaning: 'Um caractere entre os listados.',
        pattern: 'cart[ao]',
        match: 'FATURA CARTAO',
        noMatch: 'CARTUCHO',
        note: 'Acentos já são ignorados; não é necessário listar letras acentuadas.',
      },
      {
        token: '[0-9]',
        meaning: 'Intervalo — qualquer dígito de 0 a 9. Também funciona [a-z], [a-f0-9] etc.',
        pattern: 'parcela [0-9]',
        match: 'LOJA X PARCELA 3',
        noMatch: 'LOJA X PARCELA UNICA',
        note: 'Depois de "parcela " precisa vir um dígito.',
      },
      {
        token: '[^x]',
        meaning: 'Negação — qualquer caractere que NÃO esteja no conjunto.',
        pattern: 'cart[^a]',
        match: 'EMISSAO CARTOES SA',
        noMatch: 'CARTA REGISTRADA',
        note: 'Em "cartoes" após "cart" vem "o" (≠ a); em "carta" vem "a".',
      },
    ],
  },
  {
    title: 'Quantidades (n repetições)',
    rows: [
      {
        token: '?',
        meaning: 'O item anterior é opcional (0 ou 1 vez).',
        pattern: 'dep\\s?dinheiro',
        match: 'DEP DINHEIRO ATM',
        noMatch: 'DEP DE DINHEIRO',
        note: 'O \\s? permite no máximo UM espaço entre "dep" e "dinheiro".',
      },
      {
        token: '*',
        meaning: 'O item anterior repete 0 ou mais vezes (qualquer quantidade).',
        pattern: 'financ.*habit',
        match: 'FINANCIAMENTO HABITACIONAL CAIXA',
        noMatch: 'HABITACIONAL FINANCIAMENTO',
        note: 'A ordem importa: "financ" precisa vir antes de "habit".',
      },
      {
        token: '+',
        meaning: 'O item anterior repete 1 ou mais vezes (pelo menos um).',
        pattern: 'doc \\d+',
        match: 'DOC 123456 ENVIADO',
        noMatch: 'DOC ENVIADO',
        note: 'Precisa de ao menos um dígito depois de "doc ".',
      },
      {
        token: '{n}',
        meaning: 'Exatamente n repetições do item anterior.',
        pattern: 'final \\d{4}',
        match: 'FATURA CARTAO FINAL 1580',
        noMatch: 'FATURA CARTAO FINAL 58',
        note: '"58" tem só 2 dígitos; o {4} exige 4 seguidos.',
      },
      {
        token: '{n,}',
        meaning: 'n ou mais repetições.',
        pattern: '\\d{6,}',
        match: 'CNPJ 013953768000121',
        noMatch: 'PARCELA 12 DE 24',
        note: 'Exige 6+ dígitos seguidos; "12" e "24" têm só 2.',
      },
      {
        token: '{n,m}',
        meaning: 'Entre n e m repetições.',
        pattern: 'ag \\d{3,4}',
        match: 'TED AG 1234 CC 56789',
        noMatch: 'TED AG 12',
        note: 'Depois de "ag " precisa de 3 ou 4 dígitos.',
      },
    ],
  },
  {
    title: 'Classes de caracteres',
    rows: [
      {
        token: '\\d',
        meaning: 'Um dígito (0–9). O contrário é \\D (não dígito).',
        pattern: 'pedido \\d',
        match: 'IFOOD PEDIDO 7',
        noMatch: 'IFOOD PEDIDO CANCELADO',
      },
      {
        token: '\\w',
        meaning: 'Letra, dígito ou _ (um caractere). O contrário é \\W.',
        pattern: 'nf\\w',
        match: 'PAGAMENTO NF123',
        noMatch: 'PAGAMENTO NF 123',
        note: 'O espaço não é \\w — depois de "nf" precisa vir letra/dígito colado.',
      },
      {
        token: '\\s',
        meaning: 'Espaço em branco (espaço, tab). O contrário é \\S.',
        pattern: 'mercado\\spago',
        match: 'PIX MERCADO PAGO',
        noMatch: 'PIX MERCADOPAGO',
        note: 'Exige o espaço; para aceitar os dois, use mercado\\s?pago.',
      },
    ],
  },
  {
    title: 'Posição e limites',
    rows: [
      {
        token: '\\b',
        meaning: 'Fronteira de palavra — não casa dentro de outra palavra.',
        pattern: '\\bpix\\b',
        match: 'PIX ENVIADO JOAO',
        noMatch: 'PIXOTE COMERCIO LTDA',
        note: 'Em "pixote", depois de "pix" vem letra — não é fim de palavra.',
      },
      {
        token: '^',
        meaning: 'Âncora de início — o trecho precisa estar no começo da descrição.',
        pattern: '^pix',
        match: 'PIX ENVIADO JOAO',
        noMatch: 'ENVIO DE PIX JOAO',
        note: 'Sem o ^, os dois casariam.',
      },
      {
        token: '$',
        meaning: 'Âncora de fim — o trecho precisa estar no final da descrição.',
        pattern: 'fatura$',
        match: 'PAGAMENTO DE FATURA',
        noMatch: 'FATURA VISA MARCO',
        note: 'A descrição precisa terminar em "fatura".',
      },
    ],
  },
  {
    title: 'Grupos ( )',
    rows: [
      {
        token: '(abc)',
        meaning: 'Agrupa alternativas; repetir o grupo não é permitido neste sistema.',
        pattern: 'nota (fiscal|eletronica)',
        match: 'NOTA FISCAL ELETRONICA',
        noMatch: 'NOTA DE DEBITO',
      },
      {
        token: '(a|b)c',
        meaning: 'Alternativa limitada ao grupo (o OU vale só ali dentro).',
        pattern: '(deb|cred)ito',
        match: 'DEBITO AUTOMATICO ENEL',
        noMatch: 'DEPOSITO EM CONTA',
        note: '"deposito" tem "ito", mas antes vem "s", não "deb" nem "cred".',
      },
    ],
  },
  {
    title: 'Escape \\ (caracteres literais)',
    rows: [
      {
        token: '\\.',
        meaning:
          'Para casar um caractere especial de verdade, preceda com \\. São especiais: . * + ? ( ) [ ] { } | ^ $ \\',
        pattern: 'ltda\\.',
        match: 'COMERCIO SILVA LTDA.',
        noMatch: 'COMERCIO SILVA LTDA SP',
        note: 'O \\. exige o ponto; sem o \\, o . casaria qualquer caractere (até o espaço).',
      },
      {
        token: '\\(  \\)',
        meaning: 'Parênteses literais (sem escape, ( ) criam um grupo).',
        pattern: '\\(me\\)',
        match: 'PADARIA SILVA (ME)',
        noMatch: 'PADARIA SILVA ME',
      },
      {
        token: '\\*',
        meaning: 'Asterisco literal — comum em códigos de estabelecimento de cartão.',
        pattern: 'mp\\*',
        match: 'MP*FARMACIA SAO JOSE',
        noMatch: 'MP FARMACIA SAO JOSE',
        note: 'Sem o \\, o * teria o sentido de repetição.',
      },
    ],
  },
  {
    title: 'Exemplos do dia a dia',
    rows: [
      {
        token: 'literal',
        meaning: 'Cartão de crédito: um trecho literal simples resolve a maioria dos casos.',
        pattern: 'fatura cart',
        match: 'DEBITO AUT. FATURA CARTAO VISA',
        noMatch: 'FATURA DIGITAL VIVO',
      },
      {
        token: 'separadores',
        meaning: 'Pró-labore: ponto e espaços opcionais.',
        pattern: 'pro\\.?\\s*labore',
        match: 'PAGAMENTO PRO LABORE SOCIO',
        noMatch: 'PRO-LABORE MENSAL',
        note: 'O hífen não é ponto nem espaço — p/ aceitar, seria pro[-.\\s]*labore.',
      },
      {
        token: 'palavra',
        meaning: 'Impostos: \\b evita casar "darf" dentro de outra palavra.',
        pattern: '\\bdarf\\b',
        match: 'PAGAMENTO DARF 0190',
        noMatch: 'DARFANE COSMETICOS',
      },
      {
        token: 'fim de palavra',
        meaning: 'Alimentação: mercado\\b casa a palavra, não o começo de outra.',
        pattern: 'mercado\\b',
        match: 'COMPRA MERCADO SP',
        noMatch: 'MERCADOLIVRE*TECLADO',
      },
    ],
  },
];
